import CategoryPageHeader from '../CategoryPageHeader'
import ProductCardGrid from './ProductCardGrid'
import TextSeparator from '../TextSeparator'
import HorizontalScroller from '../HorizontalScroller'
import Separator from '../Separator'
import ImageSeparator from '../ImageSeparator'
import ImageSection from '../ImageSection'
import CmsButtonBlock from './CmsButtonBlock'
import ProductTestimonials from './ProductTestimonials'
import ProductsLanding from '../Products/Landing'
import Introducing from '../Products/Introducing'
import DemoVideo from '../Products/DemoVideo'
import BeforeAndAfter from '../Products/BeforeAndAfter'
import TreatmentAreas from '../Products/TreatmentAreas'
import Faq from '../Products/Faq'
import PictureInfoPanel from '../PictureInfoPanel'
import FeaturesListPanel from '../FeaturesListPanel'
import ProductVariantPanel from '../ProductVariantPanel'
import {
  LeadFormBlock,
  type LeadFormBlockProps,
} from '../LeadForm/LeadFormBlock'
import type {
  BeforeAndAfterAttributes,
  CmsButtonBlockAttributes,
  CategoryPageHeaderAttributes,
  DemoVideoAttributes,
  FaqAttributes,
  FeaturesPanelAttributes,
  HorizontalScrollerAttributes,
  ImageSectionAttributes,
  ImageSeparatorAttributes,
  IntroducingAttributes,
  PictureInfoPanelAttributes,
  ProductCardGridAttributes,
  ProductVariantPanelAttributes,
  ProductsLandingAttributes,
  TextSeparatorAttributes,
  TestimonialsAttributes,
  TreatmentAreasAttributes,
} from '../../types/cms'
import type { SeparatorAttributes } from '../../types/contactPage'
import type { CmsComponentType, StrapiComponentBlock } from '../../types/strapi'

type CmsBlockProps = StrapiComponentBlock & {
  component?: CmsComponentType | (string & {})
}

type StrapiRelationPayload<T> = {
  data?: { id?: number | string; attributes?: T } | null
}

/** CMS blocks that are intentionally not rendered in the page body. */
const IGNORED_COMPONENTS = new Set<string>([
  // Page title / meta come from attributes.seo via routePageHead — not this zone block.
  'seo.seo',
])

/** Unwrap a Strapi v4 relation on a zone block (`{ data: { attributes } }`). */
function relationAttrs<T>(relation: unknown): T | undefined {
  return (relation as StrapiRelationPayload<T> | undefined)?.data?.attributes
}

/** Renders a single Strapi dynamic-zone block by `__component` type. */
const CmsBlock = ({ component, ...props }: CmsBlockProps) => {
  switch (component) {
    case 'headers.category-page-header':
      return <CategoryPageHeader {...(props as CategoryPageHeaderAttributes)} />

    case 'cards.product-cards':
      return <ProductCardGrid {...(props as ProductCardGridAttributes)} />

    case 'separators.text-separator':
      return <TextSeparator {...(props as TextSeparatorAttributes)} />

    case 'separators.image-separator':
      return <ImageSeparator {...(props as ImageSeparatorAttributes)} />

    case 'separators.button-separator': {
      const separator = props.separator as
        | StrapiRelationPayload<SeparatorAttributes>
        | undefined
      return <Separator {...separator?.data?.attributes} />
    }

    case 'scrollers.product-scroller': {
      const horizontalScroller = props.horizontal_scroller as
        | StrapiRelationPayload<HorizontalScrollerAttributes>
        | undefined
      return <HorizontalScroller {...horizontalScroller?.data?.attributes} />
    }

    case 'button.button':
      return <CmsButtonBlock {...(props as CmsButtonBlockAttributes)} />

    // Product zone blocks are thin relation wrappers around existing collection types.
    case 'products.landing': {
      const attrs = relationAttrs<ProductsLandingAttributes>(
        props.technology_header,
      )
      return attrs ? <ProductsLanding {...attrs} /> : null
    }

    case 'products.introducing': {
      const attrs = relationAttrs<IntroducingAttributes>(props.introduction)
      return attrs ? <Introducing {...attrs} /> : null
    }

    case 'products.demo-video': {
      const attrs = relationAttrs<DemoVideoAttributes>(props.demo_video)
      return attrs ? <DemoVideo {...attrs} /> : null
    }

    case 'products.picture-info-panel': {
      const attrs = relationAttrs<PictureInfoPanelAttributes>(
        props.picture_info_panel,
      )
      return attrs ? <PictureInfoPanel {...attrs} /> : null
    }

    case 'products.before-and-after': {
      const attrs = relationAttrs<BeforeAndAfterAttributes>(
        props.before_and_after,
      )
      return attrs ? <BeforeAndAfter {...attrs} /> : null
    }

    case 'products.separator': {
      const attrs = relationAttrs<SeparatorAttributes>(props.separator)
      return attrs ? <Separator {...attrs} /> : null
    }

    case 'products.image-section': {
      const attrs = relationAttrs<ImageSectionAttributes>(props.image_section)
      return attrs ? <ImageSection {...attrs} /> : null
    }

    case 'products.treatment-areas': {
      const attrs = relationAttrs<TreatmentAreasAttributes>(
        props.treatment_area,
      )
      if (!attrs?.items?.length) return null
      return <TreatmentAreas {...attrs} />
    }

    case 'products.testimonials': {
      const attrs = relationAttrs<TestimonialsAttributes>(props.testimonial)
      return attrs ? <ProductTestimonials {...attrs} /> : null
    }

    case 'products.features-list-panel': {
      const attrs = relationAttrs<FeaturesPanelAttributes>(
        props.features_list_panel,
      )
      return attrs ? <FeaturesListPanel panels={[attrs]} /> : null
    }

    case 'products.product-variant-panel': {
      const panel = props.productVariantPanel as
        | ProductVariantPanelAttributes
        | undefined
      return panel ? <ProductVariantPanel {...panel} /> : null
    }

    case 'products.faq': {
      const attrs = relationAttrs<FaqAttributes>(props.faq)
      return attrs ? <Faq {...attrs} /> : null
    }

    case 'forms.lead-form-block':
      return <LeadFormBlock {...(props as LeadFormBlockProps)} />

    default: {
      if (component && IGNORED_COMPONENTS.has(component)) {
        return null
      }

      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console -- warn when CMS sends an unsupported block
        console.warn(
          `[CmsBlock] Unsupported Strapi component: ${component ?? '(missing __component)'}`,
        )
      }

      return null
    }
  }
}

export default CmsBlock
